import { useState, useEffect, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@clerk/react";
import {
  useUpsertContractClauseOverride,
  useRevertContractClauseOverride,
  useLockContractClauseOverride,
  useProposeContractClauseAlternate,
  usePromoteContractClauseOverrideToLibrary,
  type ContractClauseOverride,
} from "@workspace/api-client-react";

export type { ContractClauseOverride };
import {
  ChevronDown, ChevronUp, Lock, Unlock, MessageSquare, RotateCcw,
  GitCompare, Save, X, ArrowUpFromLine, ChevronRight, Info, History as HistoryIcon,
  KeyRound, Check, Ban,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { renderMergeFields, type ContractData, buildMergeContext } from "@/lib/mergeFields";
import { sanitizeHtml } from "@/lib/sanitize";
import { computeDiff, mergeAdjacentTokens } from "@/lib/textDiff";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const CATEGORY_LABELS: Record<string, string> = {
  compensation: "Compensation",
  termination: "Termination",
  buyout: "Buyout",
  nil: "NIL",
  title_ix: "Title IX",
  indemnification: "Indemnification",
  governing_law: "Governing Law",
  outside_income: "Outside Income",
  general: "General",
};

const CATEGORY_COLORS: Record<string, string> = {
  compensation: "bg-emerald-50 text-emerald-700 border-emerald-200",
  termination: "bg-red-50 text-red-700 border-red-200",
  buyout: "bg-orange-50 text-orange-700 border-orange-200",
  nil: "bg-purple-50 text-purple-700 border-purple-200",
  title_ix: "bg-blue-50 text-blue-700 border-blue-200",
  indemnification: "bg-slate-50 text-slate-700 border-slate-200",
  governing_law: "bg-gray-50 text-gray-700 border-gray-200",
  outside_income: "bg-yellow-50 text-yellow-700 border-yellow-200",
  general: "bg-muted text-muted-foreground border-border",
};

interface HistoryEvent {
  id: number;
  action: string;
  field: string | null;
  oldValue: string | null;
  newValue: string | null;
  actorClerkUserId: string | null;
  actorName: string | null;
  actorPersonas: string[];
  createdAt: string;
}

function describeHistoryEvent(ev: HistoryEvent): { label: string; bodyOld: string | null; bodyNew: string | null; detail: string | null } {
  const f = ev.field ?? "";
  if (f === "body") {
    if (ev.action === "create") return { label: "Override created", bodyOld: null, bodyNew: ev.newValue, detail: null };
    if (ev.action === "delete") return { label: "Override removed (reverted to library)", bodyOld: ev.oldValue, bodyNew: null, detail: null };
    return { label: "Body edited", bodyOld: ev.oldValue, bodyNew: ev.newValue, detail: null };
  }
  if (f === "isLocked") {
    const newLocked = ev.newValue === "true";
    return { label: newLocked ? "Locked" : "Unlocked", bodyOld: null, bodyNew: null, detail: null };
  }
  if (f === "proposedAlternate") {
    return { label: "Alternate language proposed", bodyOld: null, bodyNew: null, detail: ev.newValue };
  }
  if (f === "promotedToLibrary") {
    return {
      label: "Promoted to library",
      bodyOld: null,
      bodyNew: null,
      detail: "Override promoted to clause library as a new version.",
    };
  }
  if (f === "clauseTitle" || f === "newVersionNumber") {
    // Skip — covered by promotedToLibrary row
    return { label: "", bodyOld: null, bodyNew: null, detail: null };
  }
  if (f === "reverted") {
    return { label: "Override removed (reverted to library)", bodyOld: null, bodyNew: null, detail: null };
  }
  if (f === "unlockRequested") {
    return { label: "Unlock requested", bodyOld: null, bodyNew: null, detail: null };
  }
  if (f === "unlockReason") {
    return { label: "Unlock reason", bodyOld: null, bodyNew: null, detail: ev.newValue };
  }
  if (f === "unlockRequestResolved") {
    const status = ev.newValue ?? "";
    const label =
      status === "approved" ? "Unlock approved (clause unlocked)"
      : status === "denied" ? "Unlock denied"
      : status === "cancelled" ? "Unlock request cancelled"
      : `Unlock ${status}`;
    return { label, bodyOld: null, bodyNew: null, detail: null };
  }
  if (f === "resolutionNote") {
    return ev.newValue
      ? { label: "Resolution note", bodyOld: null, bodyNew: null, detail: ev.newValue }
      : { label: "", bodyOld: null, bodyNew: null, detail: null };
  }
  if (f === "unlockRequestId") {
    // Skip — covered by sibling unlock rows
    return { label: "", bodyOld: null, bodyNew: null, detail: null };
  }
  return { label: `${ev.action} ${f}`.trim(), bodyOld: ev.oldValue, bodyNew: ev.newValue, detail: null };
}

interface UnlockRequest {
  id: number;
  contractId: number;
  contractClauseId: number;
  status: "pending" | "approved" | "denied" | "cancelled";
  reason: string;
  requestedByClerkUserId: string | null;
  requestedByName: string | null;
  resolvedByClerkUserId: string | null;
  resolvedByName: string | null;
  resolutionNote: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function RedlineView({ original, modified }: { original: string; modified: string }) {
  const tokens = mergeAdjacentTokens(computeDiff(original, modified));
  if (tokens.every((t) => t.type === "equal")) {
    return <p className="text-sm text-muted-foreground italic">No differences from library version.</p>;
  }
  return (
    <div className="text-sm leading-relaxed font-mono whitespace-pre-wrap p-3 rounded-md border bg-muted/30">
      {tokens.map((token, i) => {
        if (token.type === "delete") {
          return <span key={i} className="bg-red-100 text-red-800 line-through">{token.text}</span>;
        }
        if (token.type === "insert") {
          return <span key={i} className="bg-green-100 text-green-800">{token.text}</span>;
        }
        return <span key={i}>{token.text}</span>;
      })}
    </div>
  );
}

type InclusionCondition = { kind: "always" } | { kind: "never" } | { kind: "conditional"; description?: string } | Record<string, unknown>;

function inclusionLabel(cond: InclusionCondition | null | undefined): string {
  if (!cond) return "Always included";
  const kind = (cond as { kind?: string }).kind;
  if (kind === "always") return "Always included";
  if (kind === "never") return "Excluded unless manually added";
  if (kind === "conditional") {
    const desc = (cond as { description?: string }).description;
    return desc ? `Conditional: ${desc}` : "Included conditionally based on contract terms";
  }
  return "Included per legal policy";
}

interface ContractClauseItem {
  id: number;
  contractId: number;
  position: number;
  clause: {
    id: number;
    title: string;
    category: string;
    jurisdiction: string | null;
    tags: string[];
    inclusionCondition?: InclusionCondition | null;
    inclusionRequired?: boolean;
    lastReviewedAt?: string | null;
    approvingAttorney?: string | null;
  };
  version: {
    id: number;
    versionNumber: number;
    body: string;
  };
}

function WhyThisClause({ cc }: { cc: ContractClauseItem }) {
  const c = cc.clause;
  const hasProvenance = c.lastReviewedAt || c.approvingAttorney;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="shrink-0 text-muted-foreground hover:text-foreground transition-colors h-7 w-7 flex items-center justify-center rounded hover:bg-muted"
          title="Why is this clause included?"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3 space-y-2.5" side="bottom" align="start">
        <div className="text-xs font-semibold text-foreground">Why this clause?</div>
        <div className="space-y-1.5">
          <div>
            <div className="text-xs text-muted-foreground">Inclusion rule</div>
            <div className="text-xs font-medium">{inclusionLabel(c.inclusionCondition)}</div>
          </div>
          {c.inclusionRequired && (
            <div>
              <div className="text-xs text-muted-foreground">Required</div>
              <div className="text-xs font-medium">Yes — this clause is mandatory</div>
            </div>
          )}
          <div>
            <div className="text-xs text-muted-foreground">Library version</div>
            <div className="text-xs font-medium">v{cc.version.versionNumber}</div>
          </div>
          {c.lastReviewedAt && (
            <div>
              <div className="text-xs text-muted-foreground">Last reviewed</div>
              <div className="text-xs font-medium">
                {new Date(c.lastReviewedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </div>
            </div>
          )}
          {c.approvingAttorney && (
            <div>
              <div className="text-xs text-muted-foreground">Approving attorney</div>
              <div className="text-xs font-medium">{c.approvingAttorney}</div>
            </div>
          )}
          {!hasProvenance && (
            <div className="text-xs text-muted-foreground italic">No review history on record.</div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface NegotiationItemRef {
  id: number;
  clauseRef: string | null;
  fieldRef: string | null;
  status: string;
  summary: string;
  raisedBy: string;
}

function clauseNegotiationItems(clauseTitle: string, items: NegotiationItemRef[]): NegotiationItemRef[] {
  const title = clauseTitle.toLowerCase();
  return items.filter((item) => {
    if (!item.clauseRef) return false;
    const ref = item.clauseRef.toLowerCase();
    return ref.includes(title) || title.includes(ref);
  });
}

function NegotiationIndicator({ items }: { items: NegotiationItemRef[] }) {
  if (items.length === 0) return null;
  const openCount = items.filter((i) => i.status === "open").length;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={`shrink-0 flex items-center gap-1 text-xs rounded px-1.5 py-0.5 border font-medium h-7 ${openCount > 0 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-muted text-muted-foreground border-border"}`}
          title={`${items.length} negotiation item${items.length > 1 ? "s" : ""} for this clause`}
        >
          <MessageSquare className="h-3 w-3" />
          {items.length}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3 space-y-2" side="bottom" align="start">
        <div className="text-xs font-semibold">Negotiation items for this clause</div>
        {items.map((item) => (
          <div key={item.id} className={`text-xs p-2 rounded border space-y-0.5 ${item.status === "open" ? "border-amber-200 bg-amber-50" : "border-border bg-muted/30"}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium">{item.raisedBy}</span>
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${item.status === "open" ? "bg-amber-100 text-amber-800" : item.status === "accepted" ? "bg-green-100 text-green-800" : item.status === "rejected" ? "bg-red-100 text-red-800" : "bg-slate-100 text-slate-700"}`}>
                {item.status}
              </span>
            </div>
            <div className="text-muted-foreground leading-snug">{item.summary}</div>
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}

interface AttorneyClauseCardProps {
  cc: ContractClauseItem;
  override: ContractClauseOverride | undefined;
  mergeContext: Record<string, string>;
  isAdmin: boolean;
  isLegal: boolean;
  contractId: number;
  negotiationItems?: NegotiationItemRef[];
  onOverrideChange: (contractClauseId: number, override: ContractClauseOverride | null) => void;
}

function AttorneyClauseCard({ cc, override, mergeContext, isAdmin, isLegal, contractId, negotiationItems = [], onOverrideChange }: AttorneyClauseCardProps) {
  const { toast } = useToast();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draftBody, setDraftBody] = useState("");
  const [showRedline, setShowRedline] = useState(false);
  const [showAlternatePanel, setShowAlternatePanel] = useState(false);
  const [alternateDraft, setAlternateDraft] = useState("");
  const [showPromoteDialog, setShowPromoteDialog] = useState(false);
  const [showRevertDialog, setShowRevertDialog] = useState(false);
  const [retireOldVersion, setRetireOldVersion] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<HistoryEvent[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [unlockRequests, setUnlockRequests] = useState<UnlockRequest[]>([]);
  const [unlockReqLoading, setUnlockReqLoading] = useState(false);
  const [showUnlockDialog, setShowUnlockDialog] = useState(false);
  const [unlockReason, setUnlockReason] = useState("");
  const [submittingUnlock, setSubmittingUnlock] = useState(false);
  const [resolvingId, setResolvingId] = useState<number | null>(null);

  const { getToken } = useAuth();
  const upsertOverride = useUpsertContractClauseOverride();
  const revertOverride = useRevertContractClauseOverride();
  const lockOverride = useLockContractClauseOverride();
  const proposeAlternate = useProposeContractClauseAlternate();
  const promoteOverride = usePromoteContractClauseOverrideToLibrary();

  const saving =
    upsertOverride.isPending ||
    revertOverride.isPending ||
    lockOverride.isPending ||
    proposeAlternate.isPending;
  const promoting = promoteOverride.isPending;

  const isLocked = override?.isLocked ?? false;
  const hasOverride = !!override?.body;
  const currentBody = override?.body ?? cc.version.body;
  const renderedBody = renderMergeFields(currentBody, mergeContext);

  const canEdit = isLegal && !isLocked;
  const canLock = isLegal;
  const canRevert = isLegal && hasOverride;
  const canPromote = isLegal && hasOverride;

  function errorMessage(err: unknown): string {
    if (err instanceof Error) return err.message;
    if (err && typeof err === "object" && "error" in err) {
      const e = (err as { error?: unknown }).error;
      if (typeof e === "string") return e;
    }
    return "Unknown error";
  }

  const loadUnlockRequests = useCallback(async () => {
    setUnlockReqLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`/api/contracts/${contractId}/clause-overrides/${cc.id}/unlock-requests`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = (await res.json()) as UnlockRequest[];
        setUnlockRequests(data);
      }
    } catch {
      /* ignore — unlock requests are best-effort UI */
    } finally {
      setUnlockReqLoading(false);
    }
  }, [cc.id, contractId, getToken]);

  // Whenever the lock state flips, refresh unlock-request data so the badge
  // and inline panel stay accurate without forcing a full page reload.
  useEffect(() => {
    void loadUnlockRequests();
  }, [loadUnlockRequests, override?.isLocked, override?.id]);

  const pendingUnlockRequest = unlockRequests.find((r) => r.status === "pending") ?? null;

  const handleSubmitUnlockRequest = async () => {
    if (!unlockReason.trim()) return;
    setSubmittingUnlock(true);
    try {
      const token = await getToken();
      const res = await fetch(`/api/contracts/${contractId}/clause-overrides/${cc.id}/unlock-requests`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ reason: unlockReason.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error ?? `Request failed: ${res.status}`);
      }
      setShowUnlockDialog(false);
      setUnlockReason("");
      setHistory(null);
      await loadUnlockRequests();
      toast({
        title: "Unlock requested",
        description: "Legal has been notified and will review your request.",
      });
    } catch (err) {
      toast({ title: "Request failed", description: errorMessage(err), variant: "destructive" });
    } finally {
      setSubmittingUnlock(false);
    }
  };

  const handleResolveUnlockRequest = async (requestId: number, action: "approve" | "deny" | "cancel", note?: string) => {
    setResolvingId(requestId);
    try {
      const token = await getToken();
      const res = await fetch(`/api/clause-unlock-requests/${requestId}/resolve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ action, note: note ?? null }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error ?? `Request failed: ${res.status}`);
      }
      const updated = (await res.json()) as UnlockRequest;
      setUnlockRequests((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      setHistory(null);
      // Approving unlocks the override server-side; reflect locally too.
      if (action === "approve" && override) {
        onOverrideChange(cc.id, { ...override, isLocked: false, lockedBy: null, lockedAt: null });
      }
      toast({
        title:
          action === "approve" ? "Unlock approved"
          : action === "deny" ? "Request denied"
          : "Request cancelled",
        description:
          action === "approve" ? "The clause is now editable by drafters."
          : action === "deny" ? "The drafter will see your decision in the change history."
          : "The pending unlock request has been withdrawn.",
      });
    } catch (err) {
      toast({ title: "Action failed", description: errorMessage(err), variant: "destructive" });
    } finally {
      setResolvingId(null);
    }
  };

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const token = await getToken();
      const res = await fetch(`/api/contract-clauses/${cc.id}/history`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error ?? `Request failed: ${res.status}`);
      }
      const data = (await res.json()) as HistoryEvent[];
      setHistory(data);
    } catch (err) {
      setHistoryError(err instanceof Error ? err.message : "Failed to load history");
    } finally {
      setHistoryLoading(false);
    }
  }, [cc.id, getToken]);

  useEffect(() => {
    if (showHistory && history === null && !historyLoading) {
      void loadHistory();
    }
  }, [showHistory, history, historyLoading, loadHistory]);

  const handleStartEdit = () => {
    setDraftBody(currentBody);
    setIsEditing(true);
    setIsExpanded(true);
  };

  const handleSaveEdit = async () => {
    if (!draftBody.trim()) return;
    try {
      const result = await upsertOverride.mutateAsync({
        contractId,
        contractClauseId: cc.id,
        data: { body: draftBody },
      });
      onOverrideChange(cc.id, result);
      setIsEditing(false);
      setHistory(null);
      toast({ title: "Override saved", description: "The clause body has been updated for this contract." });
    } catch (err) {
      toast({ title: "Save failed", description: errorMessage(err), variant: "destructive" });
    }
  };

  const handleToggleLock = async () => {
    try {
      const result = await lockOverride.mutateAsync({
        contractId,
        contractClauseId: cc.id,
        data: { locked: !isLocked },
      });
      onOverrideChange(cc.id, result);
      setHistory(null);
      toast({ title: isLocked ? "Clause unlocked" : "Clause locked", description: isLocked ? "Drafters can now edit inputs for this clause." : "Drafters will need Legal review to change inputs for this clause." });
    } catch (err) {
      toast({ title: "Error", description: errorMessage(err), variant: "destructive" });
    }
  };

  const handleSaveAlternate = async () => {
    if (!alternateDraft.trim()) return;
    try {
      const result = await proposeAlternate.mutateAsync({
        contractId,
        contractClauseId: cc.id,
        data: { proposedAlternate: alternateDraft },
      });
      onOverrideChange(cc.id, result);
      setShowAlternatePanel(false);
      setAlternateDraft("");
      setHistory(null);
      toast({ title: "Alternate proposed", description: "The proposed alternate language has been saved." });
    } catch (err) {
      toast({ title: "Error", description: errorMessage(err), variant: "destructive" });
    }
  };

  const handleRevert = async () => {
    try {
      await revertOverride.mutateAsync({ contractId, contractClauseId: cc.id });
      onOverrideChange(cc.id, null);
      setHistory(null);
      toast({ title: "Override removed", description: "Clause reverted to the library version." });
    } catch (err) {
      toast({ title: "Error", description: errorMessage(err), variant: "destructive" });
    } finally {
      setShowRevertDialog(false);
    }
  };

  const handlePromote = async () => {
    try {
      const result = await promoteOverride.mutateAsync({
        contractId,
        contractClauseId: cc.id,
        data: { retireOldVersion },
      });
      toast({
        title: "Promoted to library",
        description: `A new version (v${result.versionNumber}) has been added to the clause library${retireOldVersion ? " and the previous version was retired" : ""}.`,
      });
      setShowPromoteDialog(false);
      setRetireOldVersion(false);
      setHistory(null);
    } catch (err) {
      toast({ title: "Promote failed", description: errorMessage(err), variant: "destructive" });
    }
  };

  return (
    <div className={`border rounded-lg overflow-hidden ${isLocked ? "border-amber-300" : hasOverride ? "border-blue-200" : ""}`}>
      <div className="flex items-start gap-2 p-3 bg-muted/20">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-sm">{cc.clause.title}</span>
            <Badge variant="outline" className={`text-xs ${CATEGORY_COLORS[cc.clause.category] ?? ""}`}>
              {CATEGORY_LABELS[cc.clause.category] ?? cc.clause.category}
            </Badge>
            <span className="text-xs text-muted-foreground">v{cc.version.versionNumber}</span>
            {cc.clause.jurisdiction && (
              <span className="text-xs text-muted-foreground">· {cc.clause.jurisdiction}</span>
            )}
            <NegotiationIndicator items={negotiationItems} />
            {hasOverride && (
              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                Overridden
              </Badge>
            )}
            {isLocked && (
              <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200 gap-1">
                <Lock className="h-2.5 w-2.5" /> Locked
              </Badge>
            )}
            {override?.proposedAlternate && (
              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200 gap-1">
                <MessageSquare className="h-2.5 w-2.5" /> Alternate
              </Badge>
            )}
            {pendingUnlockRequest && (
              <Badge variant="outline" className="text-xs bg-amber-50 text-amber-800 border-amber-200 gap-1">
                <KeyRound className="h-2.5 w-2.5" /> Unlock requested
              </Badge>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
          <WhyThisClause cc={cc} />
          {canEdit && !isEditing && (
            <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={handleStartEdit}>
              Edit
            </Button>
          )}
          {canLock && !isEditing && (
            <Button
              variant="ghost"
              size="sm"
              className={`h-7 px-2 text-xs ${isLocked ? "text-amber-700 hover:bg-amber-50" : "text-muted-foreground"}`}
              onClick={handleToggleLock}
              disabled={saving}
              title={isLocked ? "Unlock clause" : "Lock clause — prevents drafter edits without Legal review"}
            >
              {isLocked ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
            </Button>
          )}
          {!isLegal && isLocked && !isEditing && !pendingUnlockRequest && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs border-amber-300 text-amber-800 hover:bg-amber-50"
              onClick={() => { setShowUnlockDialog(true); setUnlockReason(""); }}
              disabled={unlockReqLoading}
              title="Ask Legal to unlock this clause"
            >
              <KeyRound className="h-3.5 w-3.5 mr-1" />
              Request unlock
            </Button>
          )}
          {!isLegal && pendingUnlockRequest && !isEditing && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-amber-700"
              disabled
              title="Your unlock request is awaiting Legal review"
            >
              <KeyRound className="h-3.5 w-3.5 mr-1" />
              Awaiting Legal
            </Button>
          )}
          {canRevert && !isEditing && (
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground" onClick={() => setShowRevertDialog(true)} disabled={saving} title="Revert to library version">
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
          )}
          {canPromote && !isEditing && (
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground" onClick={() => setShowPromoteDialog(true)} title="Promote to library as new version">
              <ArrowUpFromLine className="h-3.5 w-3.5" />
            </Button>
          )}
          {isLegal && !isEditing && hasOverride && (
            <Button
              variant="ghost"
              size="sm"
              className={`h-7 px-2 text-xs ${showRedline ? "text-blue-700" : "text-muted-foreground"}`}
              onClick={() => { setShowRedline(!showRedline); setIsExpanded(true); }}
              title="Toggle redline view"
            >
              <GitCompare className="h-3.5 w-3.5" />
            </Button>
          )}
          {canEdit && !isEditing && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground"
              onClick={() => { setShowAlternatePanel(!showAlternatePanel); setIsExpanded(true); }}
              title="Propose alternate language"
            >
              <MessageSquare className="h-3.5 w-3.5" />
            </Button>
          )}
          {isAdmin && isLegal && !isEditing && (
            <Button
              variant="ghost"
              size="sm"
              className={`h-7 px-2 text-xs ${showHistory ? "text-blue-700" : "text-muted-foreground"}`}
              onClick={() => { setShowHistory(!showHistory); setIsExpanded(true); }}
              title="View change history"
            >
              <HistoryIcon className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="h-7 px-2 text-xs"
          >
            {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t">
          {isEditing ? (
            <div className="p-3 space-y-3">
              <div className="text-xs font-medium text-muted-foreground">Editing clause body (this contract only — library is unchanged)</div>
              <Textarea
                value={draftBody}
                onChange={(e) => setDraftBody(e.target.value)}
                className="min-h-[200px] font-mono text-sm resize-y"
                disabled={saving}
              />
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)} disabled={saving}>
                  <X className="h-3.5 w-3.5 mr-1.5" /> Cancel
                </Button>
                <Button size="sm" onClick={handleSaveEdit} disabled={saving || !draftBody.trim()}>
                  <Save className="h-3.5 w-3.5 mr-1.5" />
                  {saving ? "Saving…" : "Save override"}
                </Button>
              </div>
            </div>
          ) : showRedline && hasOverride ? (
            <div className="p-3 space-y-2">
              <div className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <GitCompare className="h-3 w-3" /> Redline: library vs. override
              </div>
              <div className="text-xs text-muted-foreground flex gap-3">
                <span><span className="bg-red-100 text-red-800 px-1 rounded">removed</span> from library</span>
                <span><span className="bg-green-100 text-green-800 px-1 rounded">added</span> in override</span>
              </div>
              <RedlineView original={cc.version.body} modified={currentBody} />
            </div>
          ) : (
            <div
              className="p-4 text-sm leading-relaxed prose prose-sm max-w-none dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderedBody) }}
            />
          )}

          {showHistory && (
            <div className="border-t p-3 space-y-3 bg-blue-50/30">
              <div className="flex items-center justify-between">
                <div className="text-xs font-medium text-blue-900 flex items-center gap-1.5">
                  <HistoryIcon className="h-3 w-3" /> Change history
                </div>
                {!historyLoading && history !== null && (
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => loadHistory()}>
                    Refresh
                  </Button>
                )}
              </div>
              {historyLoading && (
                <div className="text-xs text-muted-foreground">Loading history…</div>
              )}
              {historyError && (
                <div className="text-xs text-destructive">{historyError}</div>
              )}
              {!historyLoading && !historyError && history !== null && history.length === 0 && (
                <div className="text-xs text-muted-foreground italic">No changes have been recorded for this clause yet.</div>
              )}
              {!historyLoading && !historyError && history !== null && history.length > 0 && (
                <ol className="space-y-2">
                  {history
                    .map((ev) => ({ ev, info: describeHistoryEvent(ev) }))
                    .filter(({ info }) => info.label.length > 0)
                    .map(({ ev, info }) => (
                      <li key={ev.id} className="rounded-md border bg-background p-2 text-xs">
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <span className="font-medium text-foreground">{info.label}</span>
                          <span className="text-muted-foreground">{formatTimestamp(ev.createdAt)}</span>
                        </div>
                        <div className="text-muted-foreground mt-0.5">
                          by {ev.actorName ?? "Unknown user"}
                          {ev.actorPersonas.length > 0 && (
                            <span className="ml-1">· {ev.actorPersonas.join(", ")}</span>
                          )}
                        </div>
                        {info.detail && (
                          <div className="mt-1.5 whitespace-pre-wrap text-foreground/80 p-2 rounded bg-muted/40">
                            {info.detail}
                          </div>
                        )}
                        {(info.bodyOld !== null || info.bodyNew !== null) && (
                          <div className="mt-1.5">
                            {info.bodyOld !== null && info.bodyNew !== null ? (
                              <RedlineView original={info.bodyOld} modified={info.bodyNew} />
                            ) : info.bodyNew !== null ? (
                              <div className="font-mono whitespace-pre-wrap p-2 rounded border bg-green-50/40 text-green-900">
                                {info.bodyNew}
                              </div>
                            ) : (
                              <div className="font-mono whitespace-pre-wrap p-2 rounded border bg-red-50/40 text-red-900 line-through">
                                {info.bodyOld}
                              </div>
                            )}
                          </div>
                        )}
                      </li>
                    ))}
                </ol>
              )}
            </div>
          )}

          {(pendingUnlockRequest || (isLegal && unlockRequests.length > 0)) && (
            <div className="border-t p-3 space-y-2 bg-amber-50/40">
              <div className="text-xs font-medium text-amber-900 flex items-center gap-1.5">
                <KeyRound className="h-3 w-3" />
                {pendingUnlockRequest ? "Pending unlock request" : "Unlock request history"}
              </div>
              {(pendingUnlockRequest ? [pendingUnlockRequest] : unlockRequests.slice(0, 3)).map((reqRow) => {
                const isPending = reqRow.status === "pending";
                const statusColor =
                  reqRow.status === "approved" ? "bg-green-100 text-green-800"
                  : reqRow.status === "denied" ? "bg-red-100 text-red-800"
                  : reqRow.status === "cancelled" ? "bg-slate-100 text-slate-700"
                  : "bg-amber-100 text-amber-800";
                return (
                  <div key={reqRow.id} className={`text-xs rounded border p-2 space-y-1.5 ${isPending ? "border-amber-300 bg-background" : "border-border bg-background"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{reqRow.requestedByName ?? "Unknown drafter"}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${statusColor}`}>
                        {reqRow.status}
                      </span>
                    </div>
                    <div className="text-muted-foreground">{formatTimestamp(reqRow.createdAt)}</div>
                    <div className="whitespace-pre-wrap text-foreground/90">{reqRow.reason}</div>
                    {reqRow.resolutionNote && (
                      <div className="text-muted-foreground border-t pt-1.5 mt-1.5">
                        <span className="font-medium">Legal note:</span> {reqRow.resolutionNote}
                      </div>
                    )}
                    {!isPending && reqRow.resolvedByName && (
                      <div className="text-muted-foreground">
                        Resolved by {reqRow.resolvedByName}
                        {reqRow.resolvedAt ? ` · ${formatTimestamp(reqRow.resolvedAt)}` : ""}
                      </div>
                    )}
                    {isPending && isLegal && (
                      <div className="flex items-center gap-2 pt-1.5 border-t">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs border-green-300 text-green-700 hover:bg-green-50"
                          disabled={resolvingId === reqRow.id}
                          onClick={() => handleResolveUnlockRequest(reqRow.id, "approve")}
                          title="Approve and unlock the clause"
                        >
                          <Check className="h-3.5 w-3.5 mr-1" />
                          {resolvingId === reqRow.id ? "Working…" : "Approve & unlock"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs border-red-300 text-red-700 hover:bg-red-50"
                          disabled={resolvingId === reqRow.id}
                          onClick={() => handleResolveUnlockRequest(reqRow.id, "deny")}
                        >
                          <Ban className="h-3.5 w-3.5 mr-1" />
                          Deny
                        </Button>
                      </div>
                    )}
                    {isPending && !isLegal && (
                      <div className="flex items-center gap-2 pt-1.5 border-t">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs text-muted-foreground"
                          disabled={resolvingId === reqRow.id}
                          onClick={() => handleResolveUnlockRequest(reqRow.id, "cancel")}
                        >
                          {resolvingId === reqRow.id ? "Cancelling…" : "Withdraw request"}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {showAlternatePanel && (
            <div className="border-t p-3 space-y-2 bg-purple-50/40">
              <div className="text-xs font-medium text-purple-800 flex items-center gap-1.5">
                <MessageSquare className="h-3 w-3" /> Proposed alternate language
              </div>
              {override?.proposedAlternate && (
                <div className="text-xs text-muted-foreground p-2 rounded border bg-background">
                  <span className="font-medium">Current proposal:</span>
                  <div className="mt-1 whitespace-pre-wrap">{override.proposedAlternate}</div>
                </div>
              )}
              <Textarea
                value={alternateDraft}
                onChange={(e) => setAlternateDraft(e.target.value)}
                placeholder={override?.proposedAlternate ? "Enter updated alternate language…" : "Enter alternate language for this clause…"}
                className="min-h-[120px] text-sm resize-y"
                disabled={saving}
              />
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" size="sm" onClick={() => { setShowAlternatePanel(false); setAlternateDraft(""); }} disabled={saving}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSaveAlternate} disabled={saving || !alternateDraft.trim()}>
                  {saving ? "Saving…" : "Save alternate"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <AlertDialog open={showRevertDialog} onOpenChange={setShowRevertDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revert to library version?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the per-contract override for "{cc.clause.title}" and restore the library version. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRevert} disabled={saving} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {saving ? "Reverting…" : "Revert"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showUnlockDialog} onOpenChange={(open) => { setShowUnlockDialog(open); if (!open) setUnlockReason(""); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Request unlock from Legal</DialogTitle>
            <DialogDescription>
              "{cc.clause.title}" is locked by Legal. Send them a short note explaining what
              you need changed and why. They'll be notified by email and in the change history.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-2">
            <Textarea
              value={unlockReason}
              onChange={(e) => setUnlockReason(e.target.value)}
              placeholder="Why does this clause need to change? Include the new wording or terms you want."
              className="min-h-[140px] text-sm resize-y"
              disabled={submittingUnlock}
              autoFocus
            />
            <div className="text-xs text-muted-foreground">
              Required. Be specific so Legal can decide quickly.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUnlockDialog(false)} disabled={submittingUnlock}>
              Cancel
            </Button>
            <Button onClick={handleSubmitUnlockRequest} disabled={submittingUnlock || !unlockReason.trim()}>
              <KeyRound className="h-3.5 w-3.5 mr-1.5" />
              {submittingUnlock ? "Sending…" : "Send request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showPromoteDialog} onOpenChange={(open) => { setShowPromoteDialog(open); if (!open) setRetireOldVersion(false); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Promote to clause library?</DialogTitle>
            <DialogDescription>
              This will create a new version of "{cc.clause.title}" in the clause library based on the per-contract override. This contract will continue using its override.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-xs font-medium text-muted-foreground">New version body:</div>
            <div className="text-sm p-3 rounded-md border bg-muted/30 max-h-48 overflow-y-auto whitespace-pre-wrap">
              {currentBody}
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={retireOldVersion}
                onChange={(e) => setRetireOldVersion(e.target.checked)}
                disabled={promoting}
                className="h-4 w-4 rounded border-border"
              />
              <span className="text-sm">Retire the previous library version (mark it as superseded)</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowPromoteDialog(false); setRetireOldVersion(false); }} disabled={promoting}>Cancel</Button>
            <Button onClick={handlePromote} disabled={promoting}>
              <ArrowUpFromLine className="h-3.5 w-3.5 mr-1.5" />
              {promoting ? "Promoting…" : "Promote to library"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface AttorneyClauseViewProps {
  clauses: ContractClauseItem[];
  overrides: Map<number, ContractClauseOverride>;
  contractData: ContractData;
  contractId: number;
  isAdmin: boolean;
  isLegal: boolean;
  negotiationItems?: NegotiationItemRef[];
  onOverrideChange: (contractClauseId: number, override: ContractClauseOverride | null) => void;
}

export function AttorneyClauseView({ clauses, overrides, contractData, contractId, isAdmin, isLegal, negotiationItems = [], onOverrideChange }: AttorneyClauseViewProps) {
  const mergeContext = buildMergeContext(contractData);

  const grouped = clauses.reduce<Record<string, ContractClauseItem[]>>((acc, cc) => {
    const key = cc.clause.category;
    if (!acc[key]) acc[key] = [];
    acc[key].push(cc);
    return acc;
  }, {});

  if (clauses.length === 0) {
    return (
      <div className="py-10 text-center text-sm text-muted-foreground">
        No clauses attached to this contract yet.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!isLegal && (
        <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
          <ChevronRight className="h-4 w-4 shrink-0" />
          <span>You're viewing Attorney mode in read-only. Editing, locking, and promoting are available to Legal users.</span>
        </div>
      )}

      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat}>
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {CATEGORY_LABELS[cat] ?? cat}
            </h3>
            <span className="text-xs text-muted-foreground">({items.length})</span>
          </div>
          <div className="space-y-2">
            {items.map((cc) => (
              <AttorneyClauseCard
                key={cc.id}
                cc={cc}
                override={overrides.get(cc.id)}
                mergeContext={mergeContext}
                isAdmin={isAdmin}
                isLegal={isLegal}
                contractId={contractId}
                negotiationItems={clauseNegotiationItems(cc.clause.title, negotiationItems)}
                onOverrideChange={onOverrideChange}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
